import { useEffect, useMemo, useState } from "react";
import {
  ArrayInput,
  AutocompleteInput,
  Create,
  DateInput,
  NumberInput,
  ReferenceInput,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextInput,
  Toolbar,
  useDataProvider,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { TextField as MuiTextField } from "@mui/material";
import { Col, Row } from "../../components/grid.component";
import { Text } from "../../components/typography.component";
import { formatCPForCNPJ, normalizeDocumentForApi } from "../../utils/mask.util";
import { regexs } from "../../utils/regexs.util";

const paymentMethodChoices = [
  { id: "Pix", name: "Pix" },
  { id: "Depósito em conta", name: "Depósito em conta" },
  { id: "Cartão de Crédito", name: "Cartão de Crédito" },
  { id: "Cartão de Débito", name: "Cartão de Débito" },
  { id: "Boleto", name: "Boleto" },
  { id: "Cheque", name: "Cheque" },
  { id: "Empenho", name: "Empenho" },
];

const statusChoices = [
  { id: "Pagamento pendente", name: "Pagamento pendente" },
  { id: "Aguardando", name: "Aguardando" },
  { id: "Concluído", name: "Concluído" },
  { id: "Cancelado", name: "Cancelado" },
  { id: "Reembolsado", name: "Reembolsado" },
  { id: "Malsucedido", name: "Malsucedido" },
];

const Title = () => <Text gutterBottom={false}>Novo pedido</Text>;

const parseLocaleNumber = (value: unknown, fallback = 0) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : fallback;
  }

  if (typeof value !== "string") {
    return fallback;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return fallback;
  }

  // Accept both "10.50" and pt-BR style "10,50" (and "1.234,56").
  const normalized = trimmed.includes(",") && trimmed.includes(".")
    ? trimmed.replace(/\./g, "").replace(",", ".")
    : trimmed.replace(",", ".");

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const OrderCreateToolbar = () => (
  <Toolbar sx={{ justifyContent: "space-between" }}>
    <SaveButton />
  </Toolbar>
);

const TotalAmountAutoDisplay = () => {
  const { control, setValue } = useFormContext();
  const items = useWatch({ control, name: "items" }) || [];
  const totalDiscount = useWatch({ control, name: "totalDiscount" });

  const grossTotal = Array.isArray(items)
    ? items.reduce((acc: number, item: any) => {
        const quantity = parseLocaleNumber(item?.quantity, 0);
        const price = parseLocaleNumber(item?.price, 0);
        return acc + price * quantity;
      }, 0)
    : 0;

  const discount = parseLocaleNumber(totalDiscount, 0);
  const calculated = Math.max(0, Number((grossTotal - discount).toFixed(2)));

  useEffect(() => {
    setValue("totalAmount", calculated, { shouldDirty: true, shouldValidate: true });
  }, [calculated, setValue]);

  return (
    <>
      <TextInput source="totalAmount" sx={{ display: "none" }} />
      <MuiTextField
        label="Valor total"
        value={calculated.toFixed(2)}
        disabled
        fullWidth
        variant="filled"
        helperText="Calculado automaticamente: (soma dos itens) - desconto"
      />
    </>
  );
};

export const OrderCreate = (props: any) => {
  const [preselectedUserId, setPreselectedUserId] = useState<string | undefined>();
  const [preselectedUser, setPreselectedUser] = useState<any>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const dataProvider = useDataProvider();

  useEffect(() => {
    const selectedUserId = localStorage.getItem("selectedOrderUserId");

    if (selectedUserId && selectedUserId !== "null" && selectedUserId !== "undefined") {
      setPreselectedUserId(selectedUserId);
      dataProvider
        .getOne("users", { id: selectedUserId })
        .then(({ data }) => {
          setPreselectedUser(data);
          setLoadingUser(false);
        })
        .catch((error) => {
          console.error("Erro ao buscar usuário:", error);
          setLoadingUser(false);
        });
      localStorage.removeItem("selectedOrderUserId");
    } else {
      setLoadingUser(false);
    }
  }, [dataProvider]);

  const defaultValues = useMemo(
    () => ({
      userId: preselectedUserId,
      orderDate: new Date().toISOString().split("T")[0],
      paymentMethod: "Pix",
      status: "Aguardando",
      totalDiscount: 0,
      items: [{ quantity: 1, price: 0 }],
      billingCountry: "BR",
    }),
    [preselectedUserId]
  );

  return (
    <Create
      title={<Title />}
      redirect="list"
      transform={(data: any) => {
        const totalAmount = Math.max(
          0,
          Number(
            (((data.items || []) as any[]).reduce((acc: number, item: any) => {
              return acc + parseLocaleNumber(item?.price, 0) * parseLocaleNumber(item?.quantity, 0);
            }, 0) - parseLocaleNumber(data.totalDiscount, 0)).toFixed(2)
          )
        );

        return {
          user_id: data.userId,
          external_id: data.externalId,
          order_date: data.orderDate,
          payment_method: data.paymentMethod,
          total_discount: parseLocaleNumber(data.totalDiscount, 0),
          status: data.status,
          total_amount: totalAmount,
          billing_name: data.billingName,
          billing_doc: normalizeDocumentForApi(data.billingDoc),
          items: (data.items || []).map((item: any) => ({
            product_id: item.product_id,
            quantity: parseLocaleNumber(item.quantity, 1),
            price: parseLocaleNumber(item.price, 0),
          })),
          billing_address: {
            street: data.billingStreet,
            number: data.billingNumber,
            complement: data.billingComplement,
            neighborhood: data.billingNeighborhood,
            city: data.billingCity,
            state: data.billingState,
            postal_code: data.billingPostalCode,
            country: data.billingCountry,
          },
        };
      }}
      {...props}
    >
      <SimpleForm toolbar={<OrderCreateToolbar />} defaultValues={defaultValues}>
        <Text variant="subtitle1" display="block">
          Dados do pedido
        </Text>
        <Row spacing={3}>
          <Col sm={6}>
            {preselectedUser && !loadingUser ? (
              <MuiTextField
                label="Usuário Selecionado"
                value={`${preselectedUser.name} - ${preselectedUser.registration}`}
                disabled
                fullWidth
                variant="filled"
                helperText="Usuário pré-selecionado no diálogo"
              />
            ) : (
              <ReferenceInput
                label="Usuário"
                reference="users"
                source="userId"
                sort={{ field: "name", order: "ASC" }}
                perPage={100}
              >
                <AutocompleteInput
                  optionText={(record: any) => `${record.name} - ${record.registration || ""}`}
                  shouldRenderSuggestions={(val: string) => val.trim().length > 0}
                  filterToQuery={(searchText: string) => ({ name: searchText })}
                  noOptionsText="Digite para buscar usuários"
                  validate={required()}
                  fullWidth
                />
              </ReferenceInput>
            )}
          </Col>
          <Col sm={3}>
            <DateInput source="orderDate" label="Data do pedido" validate={required()} fullWidth />
          </Col>
          <Col sm={3}>
            <SelectInput source="paymentMethod" label="Método de pagamento" choices={paymentMethodChoices} fullWidth />
          </Col>
        </Row>
        <Row spacing={3}>
          <Col sm={6}>
            <TextInput source="externalId" label="Identificador externo" fullWidth validate={required("Identificador externo é obrigatório")} />
          </Col>
          <Col sm={6}>
            <TextInput source="billingName" label="Nome do titular" fullWidth validate={required("Nome do titular é obrigatório")} />
          </Col>
        </Row>
        <Row spacing={3}>
          <Col sm={3}>
            <SelectInput source="status" label="Status" choices={statusChoices} fullWidth validate={required()} />
          </Col>
          <Col sm={3}>
            <NumberInput source="totalDiscount" label="Desconto total" fullWidth min={0} />
          </Col>
          <Col sm={6}>
            <TotalAmountAutoDisplay />
          </Col>
        </Row>

        <Text variant="subtitle1" display="block">
          Itens do pedido
        </Text>
        <ArrayInput source="items" validate={required("Adicione pelo menos um item")}>
          <SimpleFormIterator inline>
            <ReferenceInput label="Produto" reference="products" source="product_id" perPage={100} sx={{ minWidth: 420 }}>
              <AutocompleteInput
                optionText="name"
                filterToQuery={(searchText: string) => ({ name: searchText })}
                shouldRenderSuggestions={(val: string) => val.trim().length > 1}
                noOptionsText="Digite para buscar produtos"
                validate={required("Produto é obrigatório")}
              />
            </ReferenceInput>
            <NumberInput source="quantity" label="Quantidade" min={1} validate={required("Quantidade é obrigatória")} sx={{ width: 140 }} />
            <NumberInput source="price" label="Preço" min={0} validate={required("Preço é obrigatório")} sx={{ width: 180 }} />
          </SimpleFormIterator>
        </ArrayInput>

        <Text variant="subtitle1" display="block">
          Dados de cobrança
        </Text>
        <Row spacing={3}>
          <Col sm={4}>
            <TextInput
              source="billingDoc"
              label="CPF/CNPJ do titular"
              fullWidth
              parse={(value: string) => value?.toUpperCase()}
              format={(value: string) => formatCPForCNPJ(value)}
              validate={[
                required("CPF/CNPJ do titular é obrigatório"),
                (value: string) => (!value || regexs.CPJ_AND_CNPJ_REGEX.test(value) ? undefined : "CPF ou CNPJ Inválido"),
              ]}
            />
          </Col>
        </Row>
        <Text variant="subtitle1" display="block">
          Endereço de cobrança
        </Text>
        <Row spacing={3}>
          <Col sm={8}>
            <TextInput source="billingStreet" label="Rua" fullWidth validate={required("Rua é obrigatória")} />
          </Col>
          <Col sm={2}>
            <TextInput source="billingNumber" label="Número" fullWidth validate={required("Número é obrigatório")} />
          </Col>
          <Col sm={2}>
            <TextInput source="billingComplement" label="Complemento" fullWidth />
          </Col>
        </Row>
        <Row spacing={3}>
          <Col sm={4}>
            <TextInput source="billingNeighborhood" label="Bairro" fullWidth />
          </Col>
          <Col sm={4}>
            <TextInput source="billingCity" label="Cidade" fullWidth validate={required("Cidade é obrigatória")} />
          </Col>
          <Col sm={2}>
            <TextInput source="billingState" label="UF" fullWidth validate={required("UF é obrigatória")} />
          </Col>
          <Col sm={2}>
            <TextInput source="billingPostalCode" label="CEP" fullWidth validate={required("CEP é obrigatório")} />
          </Col>
        </Row>
        <Row spacing={3}>
          <Col sm={4}>
            <TextInput source="billingCountry" label="País" fullWidth validate={required("País é obrigatório")} />
          </Col>
        </Row>
      </SimpleForm>
    </Create>
  );
};
